<main>
  <div class="content">
    <div class="grid-12">
      <!-- Sidebar -->
      <div class="span-12 lg:span-3 xl:span-2">
        <div class="info-page__rail">
          <p-iconfield>
            <p-inputicon styleClass="pi pi-search" />
            <input type="text" pInputText class="w-full" [placeholder]="text(ui.filter)" [value]="filterText()" (input)="filterText.set($any($event.target).value)" />
          </p-iconfield>

          @if (navModel().length === 0) {
            <p class="text-sm text-[var(--p-text-muted-color)] mt-3">{{ text(ui.noResults) }}</p>
          } @else {
            <p-panelmenu [model]="navModel()" [multiple]="true" styleClass="w-full mt-3 cs-nav-rail" />
          }
        </div>
      </div>

      <!-- Article -->
      <div class="span-12 lg:span-9 xl:span-10">
        @if (page(); as current) {
          <div class="flex items-start justify-between gap-4 mt-3">
            <div>
              <h1 class="text-3xl font-bold">{{ text(current.title) }}</h1>
              <p class="text-sm text-[var(--p-text-muted-color)] mt-1 max-w-3xl">{{ text(current.subtitle) }}</p>
            </div>
            <!-- Only pages with a recorded walkthrough offer one. -->
            @if (current.scribeUrl) {
              <p-button [label]="text(ui.scribe)" icon="pi pi-play-circle" (click)="openScribe()" />
            }
          </div>

          @if (isFlowPage()) {
            <!-- Tabs drive the branch selection only; the article below is rendered once and switches
                 content, so the seven steps are not duplicated per tab. -->
            <p-tabs [value]="activeBranch()?.id ?? ''" (valueChange)="selectBranch($any($event))" styleClass="mt-4">
              <p-tablist>
                @for (branch of branches(); track branch.id) {
                  <p-tab [value]="branch.id">
                    <div class="col-start-start text-left">
                      <span class="font-semibold">{{ text(branch.label) }}</span>
                      <span class="text-xs font-normal text-[var(--p-text-muted-color)]">{{ text(branch.caption) }}</span>
                    </div>
                  </p-tab>
                }
              </p-tablist>
            </p-tabs>

            <div class="grid-12 mt-4">
              <!-- Steps -->
              <div class="span-12-12-8 col">
                @for (step of steps(); track step.id; let i = $index) {
                  @let stepContent = contentFor(step);
                  <p-card [styleClass]="differsHere(step) ? 'info-step cs-card-flag-amber' : 'info-step'">
                    <div class="row-between-center gap-3 mb-3">
                      <div class="row-start-center gap-3">
                        <span class="info-step__number">{{ i + 1 }}</span>
                        <span class="text-lg font-semibold">{{ text(step.title) }}</span>
                      </div>
                      <p-tag [severity]="differsHere(step) ? 'warn' : 'secondary'">
                        <i [class]="differsHere(step) ? 'pi pi-bolt mr-1' : 'pi pi-equals mr-1'"></i>{{ stepBadge(step) }}
                      </p-tag>
                    </div>

                    @if (stepContent.pending) {
                      <!-- Marked pending rather than filled in: this branch really does differ here, and a
                           plausible guess would be read as the rule. -->
                      <p-message severity="info" styleClass="w-full" [text]="text(ui.pending)" />
                    } @else {
                      @if (stepContent.body) {
                        <p class="mb-3">{{ text(stepContent.body) }}</p>
                      }

                      @if (stepContent.bullets?.length) {
                        <ul class="info-step__bullets">
                          @for (bullet of stepContent.bullets; track bullet.term.en) {
                            <li>
                              <i class="pi pi-check"></i>
                              <span
                                ><strong>{{ text(bullet.term) }}</strong> — {{ text(bullet.description) }}</span
                              >
                            </li>
                          }
                        </ul>
                      }

                      @if (stepContent.links?.length) {
                        <div class="flex flex-wrap gap-2 mt-3">
                          @for (link of stepContent.links; track link.label.en) {
                            <p-button [label]="text(link.label)" [icon]="link.icon ?? 'pi pi-external-link'" size="small" variant="outlined" (click)="followLink(link)" />
                          }
                        </div>
                      }

                      @if (stepContent.conditions?.length) {
                        <p-panel [header]="text(ui.conditions)" styleClass="cs-panel-subtle mt-3">
                          <p-card>
                            <ul class="info-step__conditions">
                              @for (condition of stepContent.conditions; track condition.when.en) {
                                <li>
                                  <i class="pi pi-arrow-right"></i>
                                  <span>
                                    <strong>{{ text(condition.when) }}</strong> — {{ text(condition.then) }}
                                    @if (condition.link) {
                                      <p-button
                                        [label]="text(condition.link.label)"
                                        icon="pi pi-info-circle"
                                        size="small"
                                        variant="text"
                                        styleClass="align-baseline"
                                        (click)="followLink(condition.link)"
                                      />
                                    }
                                  </span>
                                </li>
                              }
                            </ul>
                          </p-card>
                        </p-panel>
                      }
                    }
                  </p-card>
                }
              </div>

              <!-- Side rail -->
              <div class="span-12-12-4">
                <div class="info-page__aside col">
                  <!-- Derived from the steps, never authored — see matrixRows. -->
                  <p-card [header]="text(ui.matrixTitle)">
                    <p-table [value]="matrixRows()" styleClass="p-datatable-sm">
                      <ng-template pTemplate="header">
                        <tr>
                          <th>{{ text(ui.matrixStep) }}</th>
                          @for (branch of branches(); track branch.id) {
                            <th class="text-center">{{ text(branch.label) }}</th>
                          }
                        </tr>
                      </ng-template>
                      <ng-template pTemplate="body" let-row>
                        <tr>
                          <td>{{ row.title }}</td>
                          @for (differs of row.differs; track $index) {
                            <td class="text-center">
                              <i [class]="differs ? 'pi pi-bolt text-amber-500' : 'pi pi-equals text-[var(--p-text-muted-color)]'"></i>
                            </td>
                          }
                        </tr>
                      </ng-template>
                    </p-table>

                    <div class="row-start-center mt-3 text-xs text-[var(--p-text-muted-color)]">
                      <span><i class="pi pi-equals mr-1"></i>{{ text(ui.legendSame) }}</span>
                      <span><i class="pi pi-bolt mr-1 text-amber-500"></i>{{ text(ui.legendDiffers) }}</span>
                    </div>
                  </p-card>

                  @if (activeBranch(); as branch) {
                    <p-card [header]="text(branch.label)">
                      <p class="text-sm">{{ text(branch.description) }}</p>
                      @if (branch.role) {
                        <p class="text-sm mt-3 row-sm-start-center"><i class="pi pi-user text-[var(--p-text-muted-color)]"></i>{{ text(branch.role) }}</p>
                      }
                      @if (branch.link) {
                        <div class="mt-3">
                          <p-button [label]="text(branch.link.label)" icon="pi pi-external-link" variant="outlined" styleClass="w-full" (click)="followLink(branch.link)" />
                        </div>
                      }
                    </p-card>
                  }
                </div>
              </div>
            </div>
          } @else {
            <!-- Concept page: prose and where to act on it. -->
            <div class="grid-12 mt-4">
              <div class="span-12-12-8 col">
                @if (current.body) {
                  <p-card>
                    <p>{{ text(current.body) }}</p>
                    @if (current.scribeUrl) {
                      <div class="mt-3">
                        <p-button [label]="text(ui.scribe)" icon="pi pi-play-circle" (click)="openScribe()" />
                      </div>
                    }
                  </p-card>
                }

                @if (current.links?.length) {
                  <div class="flex flex-wrap gap-2">
                    @for (link of current.links; track link.label.en) {
                      <p-button [label]="text(link.label)" icon="pi pi-external-link" variant="outlined" (click)="followLink(link)" />
                    }
                  </div>
                }
              </div>
            </div>
          }
        }
      </div>
    </div>
  </div>
</main>
